import useClientPagination from 'hooks/use-client-pagination'
import UrlPagination from 'components/molecules/url-pagination'
import AppointmentsApiV2 from 'api/v2/admins/appointments-api-v2'
import ArchiveFilter from 'domains/setting/components/archive-filter'
import { errorMessage } from 'domains/dashboard-migration/api'
import { ALERT_TYPES } from 'constants/alert-constants'
import TitlePage from 'components/atoms/title-page'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, CardBody, Col, Input, Row, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({ setApiErrorMsg: PropTypes.func })
}

const AppointmentPage = ({ pageUtils }) => {
  const [ appointments, setAppointments ] = useState([])
  const [ query, setQuery ] = useState('')
  const [ archived, setArchived ] = useState(false)
  const [ error, setError ] = useState('')
  const [ busy, setBusy ] = useState(false)
  const operation = useRef(false)
  const fetchSequence = useRef(0)
  const showError = useCallback(async (err) => setError(await errorMessage(err)), [])
  const [ isLoading, setIsLoading ] = useState(false)

  const fetchAppointments = useCallback((search = '') => {
    const sequence = ++fetchSequence.current
    setError('')
    setIsLoading(true)
    AppointmentsApiV2.get(search, archived)
      .then((response) => { if (sequence === fetchSequence.current) setAppointments(response.data.appointments || []) })
      .catch(showError)
      .finally(() => { if (sequence === fetchSequence.current) setIsLoading(false) })
  }, [ archived, showError ])

  useEffect(() => {
    fetchAppointments()
  }, [ fetchAppointments ])

  const handleSearch = (event) => {
    listPagination.setPage(1)
    event.preventDefault()
    fetchAppointments(query.trim())
  }

  const clearSearch = () => {
    listPagination.setPage(1)
    setQuery('')
    fetchAppointments('')
  }

  const changeArchive = async (appointment) => {
    if (operation.current) return
    const restore = Boolean(appointment.deleted_at)
    if (!window.confirm(`${restore ? 'Restore' : 'Archive'} appointment ${appointment.reference_number}? Data dan slot tetap disimpan.`)) return
    operation.current = true
    setBusy(true)
    try {
      if (restore) await AppointmentsApiV2.restore(appointment.id)
      else await AppointmentsApiV2.destroy(appointment.id)
      pageUtils?.setAlertMsg?.(`Appointment has been ${restore ? 'restored' : 'archived'}`, ALERT_TYPES.SUCCESS)
      fetchAppointments(query.trim())
    } catch (err) { showError(err) }
    finally { operation.current = false; setBusy(false) }
  }

  const listPagination = useClientPagination(appointments, { loading: isLoading })

  return (
    <>
      <TitlePage mainTitle='Setting' subTitle='Appointments' />
      { error && <div role='alert' className='alert alert-danger'>{ error }</div> }
      <Card>
        <CardBody>
          <form onSubmit={ handleSearch }>
            <Row className='mb-3 align-items-center'>
              <Col md={ 5 }><Input value={ query } onChange={ (event) => { listPagination.setPage(1); setQuery(event.target.value) } } placeholder='Search reference number' /></Col>
              <Col md={ 7 } className='mt-2 mt-md-0'>
                <Button type='submit' color='primary' className='mr-2'>{ 'Search' }</Button>
                { query && <Button type='button' color='secondary' onClick={ clearSearch }>{ 'Clear' }</Button> }
              </Col>
            </Row>
          </form>

          <ArchiveFilter value={ archived } change={ (value) => { listPagination.setPage(1); setArchived(value); setQuery('') } } disabled={ busy } />
          <Table hover responsive>
            <thead><tr><th>{ 'Reference Number' }</th><th>{ 'First Name' }</th><th>{ 'Last Name' }</th><th>{ 'Email' }</th><th>{ 'Appointment At' }</th><th>{ 'Purpose' }</th><th>{ 'Comes From' }</th><th>{ 'Action' }</th></tr></thead>
            <tbody>
              { !isLoading && listPagination.rows.map((appointment) => (
                <tr key={ appointment.id }>
                  <td>{ appointment.reference_number || '-' }</td>
                  <td>{ appointment.first_name || '-' }</td>
                  <td>{ appointment.last_name || '-' }</td>
                  <td>{ appointment.email || '-' }</td>
                  <td>{ appointment.appointment_at ? new Date(appointment.appointment_at).toLocaleString() : '-' }</td>
                  <td>{ appointment.purpose_of_appointment || '-' }</td>
                  <td>{ appointment.comes_from || '-' }</td>
                  <td><Button size='sm' color={ appointment.deleted_at ? 'success' : 'warning' } disabled={ busy } onClick={ () => changeArchive(appointment) }>{ appointment.deleted_at ? 'Restore' : 'Archive' }</Button></td>
                </tr>
              )) }
              { !isLoading && appointments.length === 0 && <tr><td colSpan='8' className='text-center text-muted'>{ 'No data' }</td></tr> }
              { isLoading && <tr><td colSpan='8' className='text-center'>{ 'Loading...' }</td></tr> }
            </tbody>
          </Table>
          <UrlPagination pagination={ listPagination } loading={ isLoading } />
        </CardBody>
      </Card>
    </>
  )
}

AppointmentPage.propTypes = propTypes
export default AppointmentPage
